import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Spinner,
  Table,
  Badge,
} from 'reactstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { BASE_URL } from '../constants/apiConfig';
import './AssignReviewerModal.css';

const AssignReviewerModal = ({ isOpen, toggle, applicationId, onSuccess }) => {
  const [reviewers, setReviewers] = useState([]);
  const [selectedReviewers, setSelectedReviewers] = useState([]);
  const [round, setRound] = useState('Round 1');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchReviewers();
      fetchExistingAssignments();
    } else {
      // Reset form when modal closes
      setSelectedReviewers([]);
      setRound('Round 1');
      setMessage('');
      setSearchTerm('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchReviewers = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/admin/reviewers`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setReviewers(response.data.reviewers || []);
    } catch (error) {
      console.error('Error fetching reviewers:', error);
      toast.error('Failed to load reviewers');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingAssignments = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/admin/applications/${applicationId}/reviewers`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      // Optionally, you can use this to show already assigned reviewers
      console.log('Existing assignments:', response.data.assignments);
    } catch (error) {
      console.error('Error fetching existing assignments:', error);
    }
  };

  const handleReviewerToggle = (reviewerId) => {
    setSelectedReviewers((prev) => {
      if (prev.includes(reviewerId)) {
        return prev.filter((id) => id !== reviewerId);
      } else {
        return [...prev, reviewerId];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredReviewers = getFilteredReviewers();
    if (selectedReviewers.length === filteredReviewers.length) {
      setSelectedReviewers([]);
    } else {
      setSelectedReviewers(filteredReviewers.map((r) => r.id));
    }
  };

  const handleSubmit = async () => {
    if (selectedReviewers.length === 0) {
      toast.warning('Please select at least one reviewer');
      return;
    }

    if (!round) {
      toast.warning('Please select a review round');
      return;
    }

    try {
      setSubmitting(true);
      const token = sessionStorage.getItem('token');
      await axios.post(
        `${BASE_URL}/api/admin/applications/${applicationId}/assign-reviewer`,
        {
          reviewerIds: selectedReviewers,
          round,
          message: message.trim() || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success(`Successfully assigned ${selectedReviewers.length} reviewer(s)`);
      if (onSuccess) {
        onSuccess();
      }
      toggle();
    } catch (error) {
      console.error('Error assigning reviewers:', error);
      toast.error(error.response?.data?.error || 'Failed to assign reviewers');
    } finally {
      setSubmitting(false);
    }
  };

  const getFilteredReviewers = () => {
    if (!searchTerm) return reviewers;
    const term = searchTerm.toLowerCase();
    return reviewers.filter(
      (reviewer) =>
        reviewer.name.toLowerCase().includes(term) ||
        reviewer.email.toLowerCase().includes(term)
    );
  };

  const filteredReviewers = getFilteredReviewers();

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="assign-reviewer-modal">
      <ModalHeader toggle={toggle} className="assign-reviewer-header">
        <i className="bi bi-person-plus me-2"></i>
        Assign Reviewers
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-3">Loading reviewers...</p>
          </div>
        ) : (
          <>
            {/* Round Selection */}
            <FormGroup>
              <Label for="roundSelect" className="fw-bold">
                Review Round <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                id="roundSelect"
                value={round}
                onChange={(e) => setRound(e.target.value)}
                className="form-select"
              >
                <option value="Round 1">Round 1</option>
                <option value="Round 2">Round 2</option>
                <option value="Final Round">Final Round</option>
              </Input>
            </FormGroup>

            {/* Optional Message */}
            <FormGroup>
              <Label for="messageInput" className="fw-bold">
                Message (Optional)
              </Label>
              <Input
                type="textarea"
                id="messageInput"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add a note or instructions for the reviewer(s)..."
                rows="3"
                maxLength="500"
              />
              <small className="text-muted">{message.length}/500 characters</small>
            </FormGroup>

            {/* Search Input */}
            <FormGroup>
              <Label for="searchInput" className="fw-bold">
                Search Reviewers
              </Label>
              <Input
                type="text"
                id="searchInput"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                className="search-input"
              />
            </FormGroup>

            {/* Reviewers Table */}
            <div className="reviewers-table-container">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                  Select Reviewers ({selectedReviewers.length} selected)
                </h6>
                <Button
                  color="link"
                  size="sm"
                  onClick={handleSelectAll}
                  className="text-decoration-none"
                >
                  {selectedReviewers.length === filteredReviewers.length
                    ? 'Deselect All'
                    : 'Select All'}
                </Button>
              </div>

              {filteredReviewers.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  {searchTerm ? 'No reviewers found matching your search' : 'No reviewers available'}
                </div>
              ) : (
                <Table bordered hover responsive className="reviewers-table">
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }} className="text-center">
                        Select
                      </th>
                      <th>Name</th>
                      <th>Email</th>
                      <th style={{ width: '100px' }}>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReviewers.map((reviewer) => (
                      <tr
                        key={reviewer.id}
                        className={`reviewer-row ${
                          selectedReviewers.includes(reviewer.id) ? 'selected' : ''
                        }`}
                        onClick={() => handleReviewerToggle(reviewer.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="text-center">
                          <Input
                            type="checkbox"
                            checked={selectedReviewers.includes(reviewer.id)}
                            onChange={() => handleReviewerToggle(reviewer.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td>{reviewer.name}</td>
                        <td>
                          <a
                            href={`mailto:${reviewer.email}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {reviewer.email}
                          </a>
                        </td>
                        <td>
                          <Badge
                            color={reviewer.role === 'admin' ? 'danger' : 'primary'}
                            className="role-badge"
                          >
                            {reviewer.role}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter className="assign-reviewer-footer">
        <Button color="secondary" onClick={toggle} disabled={submitting}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={submitting || selectedReviewers.length === 0 || loading}
        >
          {submitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              Assigning...
            </>
          ) : (
            <>
              <i className="bi bi-check-circle me-2"></i>
              Assign {selectedReviewers.length > 0 ? `(${selectedReviewers.length})` : ''}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AssignReviewerModal;
